import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";
import { FiCheck, FiChevronDown, FiChevronRight, FiCopy } from "react-icons/fi";

interface JSONTreeNodeProps {
    keyName: string | null;
    value: unknown;
    depth: number;
    defaultExpanded?: boolean;
    isLast?: boolean;
}

function isObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isArray(value: unknown): value is unknown[] {
    return Array.isArray(value);
}

function JSONTreeNode({
    keyName,
    value,
    depth,
    defaultExpanded = true,
    isLast = false,
}: JSONTreeNodeProps) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded && depth < 3);

    const isExpandable = isObject(value) || isArray(value);
    const isEmpty =
        (isObject(value) && Object.keys(value).length === 0) ||
        (isArray(value) && value.length === 0);

    const getBrackets = () => {
        if (isArray(value)) return { open: "[", close: "]" };
        if (isObject(value)) return { open: "{", close: "}" };
        return { open: "", close: "" };
    };

    const brackets = getBrackets();
    const indent = depth * 16;

    const renderValue = () => {
        if (value === null) {
            return (
                <span className="text-orange-400 dark:text-orange-300">
                    null
                </span>
            );
        }
        if (typeof value === "boolean") {
            return (
                <span className="text-purple-500 dark:text-purple-400">
                    {value.toString()}
                </span>
            );
        }
        if (typeof value === "number") {
            return (
                <span className="text-blue-500 dark:text-blue-400">
                    {value}
                </span>
            );
        }
        if (typeof value === "string") {
            return (
                <span className="text-green-600 dark:text-green-400">
                    "{value}"
                </span>
            );
        }
        return <span className="text-slate-600">{String(value)}</span>;
    };

    const getPreviewLength = () => {
        if (isArray(value)) return value.length;
        if (isObject(value)) return Object.keys(value).length;
        return 0;
    };

    if (!isExpandable) {
        return (
            <div
                className="flex items-center py-0.5 font-mono text-sm"
                style={{ paddingLeft: indent }}
            >
                {keyName !== null && (
                    <>
                        <span className="text-rose-500 dark:text-rose-400">
                            "{keyName}"
                        </span>
                        <span className="text-slate-500">: </span>
                    </>
                )}
                {renderValue()}
                {!isLast && <span className="text-slate-500">,</span>}
            </div>
        );
    }

    if (isEmpty) {
        return (
            <div
                className="flex items-center py-0.5 font-mono text-sm"
                style={{ paddingLeft: indent }}
            >
                {keyName !== null && (
                    <>
                        <span className="text-rose-500 dark:text-rose-400">
                            "{keyName}"
                        </span>
                        <span className="text-slate-500">: </span>
                    </>
                )}
                <span className="text-slate-500">
                    {brackets.open}
                    {brackets.close}
                </span>
                {!isLast && <span className="text-slate-500">,</span>}
            </div>
        );
    }

    const entries = isArray(value)
        ? value.map((v, i) => [i.toString(), v] as [string, unknown])
        : Object.entries(value);

    return (
        <div>
            <div
                className="flex cursor-pointer items-center py-0.5 font-mono text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
                style={{ paddingLeft: indent }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <span className="mr-1 text-slate-400">
                    {isExpanded ? (
                        <FiChevronDown className="h-3 w-3" />
                    ) : (
                        <FiChevronRight className="h-3 w-3" />
                    )}
                </span>
                {keyName !== null && (
                    <>
                        <span className="text-rose-500 dark:text-rose-400">
                            "{keyName}"
                        </span>
                        <span className="text-slate-500">: </span>
                    </>
                )}
                <span className="text-slate-500">{brackets.open}</span>
                {!isExpanded && (
                    <>
                        <span className="mx-1 text-slate-400">
                            {getPreviewLength()}{" "}
                            {isArray(value) ? "items" : "keys"}
                        </span>
                        <span className="text-slate-500">{brackets.close}</span>
                        {!isLast && <span className="text-slate-500">,</span>}
                    </>
                )}
            </div>
            {isExpanded && (
                <>
                    {entries.map(([k, v], idx) => (
                        <JSONTreeNode
                            key={k}
                            keyName={isArray(value) ? null : k}
                            value={v}
                            depth={depth + 1}
                            defaultExpanded={defaultExpanded}
                            isLast={idx === entries.length - 1}
                        />
                    ))}
                    <div
                        className="py-0.5 font-mono text-sm text-slate-500"
                        style={{ paddingLeft: indent }}
                    >
                        {brackets.close}
                        {!isLast && <span>,</span>}
                    </div>
                </>
            )}
        </div>
    );
}

interface JSONTreeViewProps {
    data: unknown;
    className?: string;
    defaultExpanded?: boolean;
}

export function JSONTreeView({
    data,
    className,
    defaultExpanded = true,
}: JSONTreeViewProps) {
    return (
        <div
            className={clsx(
                "overflow-auto rounded-lg bg-slate-50 p-4 dark:bg-slate-800",
                className,
            )}
        >
            <JSONTreeNode
                keyName={null}
                value={data}
                depth={0}
                defaultExpanded={defaultExpanded}
            />
        </div>
    );
}

interface CopyButtonProps {
    text: string;
    className?: string;
}

export function CopyButton({ text, className }: CopyButtonProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy:", err);
        }
    }, [text]);

    return (
        <button
            onClick={handleCopy}
            className={clsx(
                "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                copied
                    ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                    : "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600",
                className,
            )}
        >
            {copied ? (
                <>
                    <FiCheck className="h-4 w-4" />
                    Copied!
                </>
            ) : (
                <>
                    <FiCopy className="h-4 w-4" />
                    Copy
                </>
            )}
        </button>
    );
}

interface XMLPrettyViewProps {
    xml: string;
    className?: string;
}

interface XMLNode {
    type: "element" | "text" | "declaration";
    name?: string;
    attributes?: Record<string, string>;
    children?: XMLNode[];
    value?: string;
}

function parseXML(xml: string): XMLNode[] {
    const nodes: XMLNode[] = [];
    let pos = 0;

    const parseAttributes = (attrString: string): Record<string, string> => {
        const attrs: Record<string, string> = {};
        const regex = /([\w-:]+)\s*=\s*"([^"]*)"/g;
        let match;
        while ((match = regex.exec(attrString)) !== null) {
            attrs[match[1]] = match[2];
        }
        return attrs;
    };

    const parseNodes = (): XMLNode[] => {
        const result: XMLNode[] = [];

        while (pos < xml.length) {
            // Skip whitespace
            while (pos < xml.length && /\s/.test(xml[pos])) pos++;
            if (pos >= xml.length) break;

            if (xml[pos] === "<") {
                // Check for declaration <?xml ... ?>
                if (xml.slice(pos, pos + 2) === "<?") {
                    const endDecl = xml.indexOf("?>", pos);
                    if (endDecl !== -1) {
                        const declContent = xml.slice(pos + 2, endDecl).trim();
                        const spaceIdx = declContent.indexOf(" ");
                        const name =
                            spaceIdx > 0
                                ? declContent.slice(0, spaceIdx)
                                : declContent;
                        const attrStr =
                            spaceIdx > 0 ? declContent.slice(spaceIdx) : "";
                        result.push({
                            type: "declaration",
                            name,
                            attributes: parseAttributes(attrStr),
                        });
                        pos = endDecl + 2;
                        continue;
                    }
                }

                // Check for closing tag
                if (xml.slice(pos, pos + 2) === "</") {
                    // We've hit a closing tag, return to parent
                    break;
                }

                // Opening tag
                const tagMatch = xml
                    .slice(pos)
                    .match(/^<([\w-:]+)([^>]*?)(\/?)>/);
                if (tagMatch) {
                    const tagName = tagMatch[1];
                    const attrStr = tagMatch[2];
                    const selfClosing = tagMatch[3] === "/";
                    pos += tagMatch[0].length;

                    const node: XMLNode = {
                        type: "element",
                        name: tagName,
                        attributes: parseAttributes(attrStr),
                        children: [],
                    };

                    if (!selfClosing) {
                        // Parse children
                        node.children = parseNodes();

                        // Consume closing tag
                        const closeMatch = xml
                            .slice(pos)
                            .match(new RegExp(`^</${tagName}\\s*>`));
                        if (closeMatch) {
                            pos += closeMatch[0].length;
                        }
                    }

                    result.push(node);
                } else {
                    pos++;
                }
            } else {
                // Text content
                const textEnd = xml.indexOf("<", pos);
                const text =
                    textEnd === -1 ? xml.slice(pos) : xml.slice(pos, textEnd);
                const trimmedText = text.trim();
                if (trimmedText) {
                    result.push({
                        type: "text",
                        value: trimmedText,
                    });
                }
                pos = textEnd === -1 ? xml.length : textEnd;
            }
        }

        return result;
    };

    return parseNodes();
}

interface XMLTreeNodeProps {
    node: XMLNode;
    depth: number;
    isLast?: boolean;
}

function XMLTreeNode({ node, depth }: XMLTreeNodeProps) {
    const [isExpanded, setIsExpanded] = useState(depth < 3);
    const indent = depth * 16;

    if (node.type === "text") {
        return (
            <div
                className="py-0.5 font-mono text-sm"
                style={{ paddingLeft: indent }}
            >
                <span className="text-slate-700 dark:text-slate-300">
                    {node.value}
                </span>
            </div>
        );
    }

    if (node.type === "declaration") {
        return (
            <div
                className="py-0.5 font-mono text-sm"
                style={{ paddingLeft: indent }}
            >
                <span className="text-slate-500 dark:text-slate-400">
                    {"<?"}
                    {node.name}
                </span>
                {node.attributes &&
                    Object.entries(node.attributes).map(([key, value]) => (
                        <span key={key}>
                            <span className="text-purple-500 dark:text-purple-400">
                                {" "}
                                {key}
                            </span>
                            <span className="text-slate-500">=</span>
                            <span className="text-green-600 dark:text-green-400">
                                "{value}"
                            </span>
                        </span>
                    ))}
                <span className="text-slate-500 dark:text-slate-400">
                    {"?>"}
                </span>
            </div>
        );
    }

    const hasChildren = node.children && node.children.length > 0;
    const hasOnlyTextChild =
        hasChildren &&
        node.children!.length === 1 &&
        node.children![0].type === "text";

    // Simple element with only text content - render inline
    if (hasOnlyTextChild) {
        return (
            <div
                className="py-0.5 font-mono text-sm"
                style={{ paddingLeft: indent }}
            >
                <span className="text-blue-500 dark:text-blue-400">
                    {"<"}
                    {node.name}
                </span>
                {node.attributes &&
                    Object.entries(node.attributes).map(([key, value]) => (
                        <span key={key}>
                            <span className="text-purple-500 dark:text-purple-400">
                                {" "}
                                {key}
                            </span>
                            <span className="text-slate-500">=</span>
                            <span className="text-green-600 dark:text-green-400">
                                "{value}"
                            </span>
                        </span>
                    ))}
                <span className="text-blue-500 dark:text-blue-400">{">"}</span>
                <span className="text-slate-700 dark:text-slate-300">
                    {node.children![0].value}
                </span>
                <span className="text-blue-500 dark:text-blue-400">
                    {"</"}
                    {node.name}
                    {">"}
                </span>
            </div>
        );
    }

    // Empty element or self-closing
    if (!hasChildren) {
        return (
            <div
                className="py-0.5 font-mono text-sm"
                style={{ paddingLeft: indent }}
            >
                <span className="text-blue-500 dark:text-blue-400">
                    {"<"}
                    {node.name}
                </span>
                {node.attributes &&
                    Object.entries(node.attributes).map(([key, value]) => (
                        <span key={key}>
                            <span className="text-purple-500 dark:text-purple-400">
                                {" "}
                                {key}
                            </span>
                            <span className="text-slate-500">=</span>
                            <span className="text-green-600 dark:text-green-400">
                                "{value}"
                            </span>
                        </span>
                    ))}
                <span className="text-blue-500 dark:text-blue-400">
                    {" />"}
                </span>
            </div>
        );
    }

    // Element with children - expandable
    return (
        <div>
            <div
                className="flex cursor-pointer items-center py-0.5 font-mono text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
                style={{ paddingLeft: indent }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <span className="mr-1 text-slate-400">
                    {isExpanded ? (
                        <FiChevronDown className="h-3 w-3" />
                    ) : (
                        <FiChevronRight className="h-3 w-3" />
                    )}
                </span>
                <span className="text-blue-500 dark:text-blue-400">
                    {"<"}
                    {node.name}
                </span>
                {node.attributes &&
                    Object.entries(node.attributes).map(([key, value]) => (
                        <span key={key}>
                            <span className="text-purple-500 dark:text-purple-400">
                                {" "}
                                {key}
                            </span>
                            <span className="text-slate-500">=</span>
                            <span className="text-green-600 dark:text-green-400">
                                "{value}"
                            </span>
                        </span>
                    ))}
                <span className="text-blue-500 dark:text-blue-400">{">"}</span>
                {!isExpanded && (
                    <>
                        <span className="mx-1 text-slate-400">
                            {node.children!.length}{" "}
                            {node.children!.length === 1 ? "child" : "children"}
                        </span>
                        <span className="text-blue-500 dark:text-blue-400">
                            {"</"}
                            {node.name}
                            {">"}
                        </span>
                    </>
                )}
            </div>
            {isExpanded && (
                <>
                    {node.children!.map((child, idx) => (
                        <XMLTreeNode
                            key={idx}
                            node={child}
                            depth={depth + 1}
                            isLast={idx === node.children!.length - 1}
                        />
                    ))}
                    <div
                        className="py-0.5 font-mono text-sm text-blue-500 dark:text-blue-400"
                        style={{ paddingLeft: indent }}
                    >
                        {"</"}
                        {node.name}
                        {">"}
                    </div>
                </>
            )}
        </div>
    );
}

export function XMLPrettyView({ xml, className }: XMLPrettyViewProps) {
    const parsedNodes = useMemo(() => {
        try {
            return parseXML(xml);
        } catch {
            return null;
        }
    }, [xml]);

    if (!parsedNodes) {
        // Fallback to plain text if parsing fails
        return (
            <div
                className={clsx(
                    "overflow-auto rounded-lg bg-slate-50 p-4 dark:bg-slate-800",
                    className,
                )}
            >
                <pre className="font-mono text-sm whitespace-pre-wrap text-slate-700 dark:text-slate-300">
                    {xml}
                </pre>
            </div>
        );
    }

    return (
        <div
            className={clsx(
                "overflow-auto rounded-lg bg-slate-50 p-4 dark:bg-slate-800",
                className,
            )}
        >
            {parsedNodes.map((node, idx) => (
                <XMLTreeNode
                    key={idx}
                    node={node}
                    depth={0}
                    isLast={idx === parsedNodes.length - 1}
                />
            ))}
        </div>
    );
}
